/**
 * Tech Request Service Layer
 * Handles all Firestore operations for the tech_requests collection
 * (Support log for technical questions and requests)
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  TechRequest,
  TechRequestPriority,
  TechRequestStatus,
} from '../types/theo-workflow.types';
import { getTenantIdSync, tenantIdReadUnion } from '../hooks/useTenantId';

const COLLECTION_NAME = 'tech_requests';

// ============================================================================
// CREATE
// ============================================================================

/**
 * Create a new tech request
 */
export async function createTechRequest(
  data: Omit<TechRequest, 'id' | 'createdAt' | 'updatedAt' | 'status'>,
  userId?: string
): Promise<string> {
  const requestData: Omit<TechRequest, 'id'> = {
    ...data,
    tenantId: data.tenantId ?? getTenantIdSync(),
    status: TechRequestStatus.OPEN,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    createdBy: userId,
  } as Omit<TechRequest, 'id'>;

  const docRef = await addDoc(collection(db, COLLECTION_NAME), requestData);
  return docRef.id;
}

// ============================================================================
// READ
// ============================================================================

/**
 * Get a single tech request by ID
 */
export async function getTechRequest(
  requestId: string
): Promise<TechRequest | null> {
  const docRef = doc(db, COLLECTION_NAME, requestId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as TechRequest;
}

/**
 * Get all tech requests for a specific deal
 */
export async function getTechRequestsByDeal(dealId: string): Promise<TechRequest[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('dealId', '==', dealId),
    orderBy('createdAt', 'desc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as TechRequest
  );
}

/**
 * Get tech requests by status
 */
export async function getTechRequestsByStatus(
  status: TechRequestStatus
): Promise<TechRequest[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('status', '==', status),
    orderBy('createdAt', 'desc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as TechRequest
  );
}

/**
 * Get tech requests by priority
 */
export async function getTechRequestsByPriority(
  priority: TechRequestPriority
): Promise<TechRequest[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('priority', '==', priority),
    orderBy('createdAt', 'desc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as TechRequest
  );
}

/**
 * Get all open tech requests, ordered by priority (HIGH -> MEDIUM -> LOW)
 */
export async function getOpenTechRequests(): Promise<TechRequest[]> {
  // Note: Firestore doesn't support ordering by enum priority directly
  // We fetch all open requests and sort in-memory
  const openRequests = await getTechRequestsByStatus(TechRequestStatus.OPEN);

  const priorityOrder = {
    [TechRequestPriority.HIGH]: 0,
    [TechRequestPriority.MEDIUM]: 1,
    [TechRequestPriority.LOW]: 2,
  };

  return openRequests.sort(
    (a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]
  );
}

// ============================================================================
// UPDATE
// ============================================================================

/**
 * Update a tech request
 */
export async function updateTechRequest(
  requestId: string,
  data: Partial<Omit<TechRequest, 'id' | 'createdAt' | 'createdBy' | 'dealId'>>
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, requestId);

  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Update the status of a tech request
 */
export async function updateTechRequestStatus(
  requestId: string,
  status: TechRequestStatus
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, requestId);

  const updateData: Record<string, unknown> = {
    status,
    updatedAt: Timestamp.now(),
  };

  // If resolving, set resolved timestamp
  if (status === TechRequestStatus.RESOLVED || status === TechRequestStatus.CLOSED) {
    updateData.resolvedAt = Timestamp.now();
  }

  await updateDoc(docRef, updateData);
}

/**
 * Resolve a tech request with an answer
 */
export async function resolveTechRequest(
  requestId: string,
  answer: string,
  userId?: string
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, requestId);

  await updateDoc(docRef, {
    answer,
    status: TechRequestStatus.RESOLVED,
    resolvedAt: Timestamp.now(),
    resolvedBy: userId,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Update the priority of a tech request
 */
export async function updateTechRequestPriority(
  requestId: string,
  priority: TechRequestPriority
): Promise<void> {
  await updateTechRequest(requestId, { priority });
}

// ============================================================================
// DELETE
// ============================================================================

/**
 * Delete a tech request
 */
export async function deleteTechRequest(requestId: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, requestId);
  await deleteDoc(docRef);
}

/**
 * Delete all tech requests for a deal
 */
export async function deleteTechRequestsByDeal(dealId: string): Promise<void> {
  const requests = await getTechRequestsByDeal(dealId);

  // Delete all requests (use batch for better performance with many requests)
  const deletePromises = requests.map((request) => {
    if (request.id) {
      return deleteTechRequest(request.id);
    }
    return Promise.resolve();
  });

  await Promise.all(deletePromises);
}
